import { X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import SharedSelect from "@/components/shared/select";
import { Input } from "@/components/ui/input";
import MultiSelect, {
  type SelectOption as Option,
} from "@/components/shared/multi-select/Select";
import { useBrands } from "@/api/useBrands";
import { useExploreValues } from "@/api/useExplore";
import { useGetTopics } from "@/api/useGetTopics";
import {
  ACV_METADATA_FIELDS,
  AllowedFilterFields,
} from "@/api/ticket-event/ticket-event.model";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";
import { useExploreCustomFields } from "@/v2/features/explore/useExploreCustomFields";

type Rule = {
  id: string;
  field: string;
  operator: string;
  value: string[];
};

interface FilterRuleProps {
  rule: Rule;
  onUpdate: (updatedRule: Partial<Rule>) => void;
  onRemove: () => void;
}

export function FilterRule({ rule, onUpdate, onRemove }: FilterRuleProps) {
  const [brandOptions, setBrandOptions] = useState<Option[]>([]);
  const { workspace } = useWorkspace();
  const [channelOptions, setChannelOptions] = useState<Option[]>([]);
  const [topicOptions, setTopicOptions] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: brands } = useBrands();
  const { data: values } = useExploreValues(true);
  const { data: topics } = useGetTopics();
  const customFieldsData = useExploreCustomFields();

  const customFields =
    workspace === "walkme"
      ? [
          {
            value: AllowedFilterFields.CUSTOM_ARR,
            label: "ARR",
            type: "number",
          },
          {
            value: AllowedFilterFields.CUSTOM_ARR_GLOBAL,
            label: "Global ARR",
            type: "number",
          },
          {
            value: AllowedFilterFields.ACCOUNT_TYPE,
            label: "Account Type",
          },
          {
            value: AllowedFilterFields.ORIGIN,
            label: "Origin",
          },
          {
            value: AllowedFilterFields.SHIPPING_COUNTRY,
            label: "Shipping Country",
          },
          {
            value: AllowedFilterFields.CUSTOM_ACCOUNT_NAME,
            label: "Account Name",
          },
        ]
      : [];

  // Dynamic custom fields from metadata
  const dynamicCustomFields = customFieldsData.map((field) => ({
    value: `metadata.${field.id}`,
    label: field.label,
  }));

  const fields = [
    { value: AllowedFilterFields.BRAND, label: "Brand" },
    { value: AllowedFilterFields.CHANNEL, label: "Channel" },
    { value: AllowedFilterFields.TICKET_FIELDS, label: "Ticket Fields" },
    { value: AllowedFilterFields.TOPIC, label: "Topic" },
    { value: AllowedFilterFields.TAG, label: "Tag" },
    {
      value: AllowedFilterFields.REGULAR_ATTACHMENT,
      label: "Regular Attachment",
    },
    {
      value: AllowedFilterFields.INLINE_ATTACHMENT,
      label: "Inline Attachment",
    },
    {
      value: AllowedFilterFields.EMAIL,
      label: "Email",
    },
    ...customFields,
    ...dynamicCustomFields,
  ];

  const operators = [
    { value: "is", label: "is" },
    { value: "is_not", label: "is not" },
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "not contains" },
    { value: "is_null", label: "is null" },
  ];

  useEffect(() => {
    // Fetch options when the field changes
    if (rule.field) {
      fetchOptionsForField(rule.field);
    }
  }, [rule.field]);

  const fetchOptionsForField = async (field: string) => {
    setIsLoading(true);
    setError(null);
    try {
      switch (field) {
        case "brand":
          if (brandOptions.length === 0) {
            setBrandOptions(
              brands?.map((brand: any) => ({
                value: String(brand.id),
                label: brand.name,
              })) ?? []
            );
          }
          break;
        case "channel":
          if (channelOptions.length === 0) {
            setChannelOptions(
              values?.channels?.map((channel: any) => ({
                value: String(channel.value),
                label: channel.label,
              })) ?? []
            );
          }
          break;
        case "topic":
          if (topicOptions.length === 0) {
            setTopicOptions(
              topics?.map((topic: any) => ({
                value: String(topic.id),
                label: topic.topic,
              })) ?? []
            );
          }
          break;
      }
    } catch (error) {
      console.error("Error fetching options:", error);
      setError(
        error instanceof Error ? error.message : "Failed to load options"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getOperatorsForField = (field: string) => {
    if (
      field === AllowedFilterFields.REGULAR_ATTACHMENT ||
      field === AllowedFilterFields.INLINE_ATTACHMENT
    ) {
      return [{ value: "is", label: "is" }];
    }

    if (field === AllowedFilterFields.CUSTOM_ARR) {
      return [
        { value: "gt", label: "is greater than" },
        { value: "lt", label: "is less than" },
      ];
    }
    if (field === AllowedFilterFields.CUSTOM_ARR_GLOBAL) {
      return [
        { value: "gt", label: "is greater than" },
        { value: "lt", label: "is less than" },
      ];
    }

    const isStringField = field === AllowedFilterFields.EMAIL;

    if (isStringField) {
      return operators;
    }

    if (ACV_METADATA_FIELDS.includes(field)) {
      return [
        { value: "is", label: "is" },
        { value: "is_not", label: "is not" },
        { value: "is_null", label: "is null" },
        { value: "gt", label: "Above" },
        { value: "lt", label: "Below" },
      ];
    }

    return [
      { value: "is", label: "is" },
      { value: "is_not", label: "is not" },
      { value: "is_null", label: "is null" },
    ];
  };

  const getOptionsForField = useCallback(
    (field: string): Option[] => {
      switch (field) {
        case "brand":
          return brands && Array.isArray(brands)
            ? brands?.map((brand: any) => ({
                value: String(brand.id),
                label: brand.name,
              }))
            : [];
        case "channel":
          return values && Array.isArray(values.channels)
            ? values.channels?.map((channel: any) => ({
                value: String(channel.value),
                label: channel.label,
              }))
            : [];
        case "topic":
          return topics && Array.isArray(topics)
            ? topics?.map((topic: any) => ({
                value: String(topic.id),
                label: topic.topic,
              }))
            : [];
        default:
          return [];
      }
    },
    [brands, values, topics]
  );

  const isNullOperator = rule.operator === "is_null";
  const isKeyValue = rule.field === "ticket_fields";
  const isString =
    rule.field === "tag" ||
    rule.field === "email" ||
    rule.field === AllowedFilterFields.ORIGIN ||
    rule.field === AllowedFilterFields.SHIPPING_COUNTRY ||
    rule.field === AllowedFilterFields.CUSTOM_ACCOUNT_NAME ||
    rule.field === AllowedFilterFields.ACCOUNT_TYPE;
  const isNumber =
    rule.field === AllowedFilterFields.CUSTOM_ARR ||
    rule.field === AllowedFilterFields.CUSTOM_ARR_GLOBAL;
  const isBoolean =
    rule.field === AllowedFilterFields.REGULAR_ATTACHMENT ||
    rule.field === AllowedFilterFields.INLINE_ATTACHMENT;
  const isCustomMetadataField =
    typeof rule.field === "string" &&
    rule.field.startsWith("metadata.") &&
    rule.field !== AllowedFilterFields.CUSTOM_ARR &&
    rule.field !== AllowedFilterFields.CUSTOM_ARR_GLOBAL &&
    rule.field !== AllowedFilterFields.ACCOUNT_TYPE &&
    rule.field !== AllowedFilterFields.ORIGIN &&
    rule.field !== AllowedFilterFields.SHIPPING_COUNTRY &&
    rule.field !== AllowedFilterFields.CUSTOM_ACCOUNT_NAME;

  const isNumericOperator = rule.operator === "gt" || rule.operator === "lt";

  return (
    <div className="flex flex-wrap items-center gap-2 w-full">
      <div className="flex items-center gap-2 flex-1">
        <SharedSelect
          options={fields}
          value={rule.field || ""}
          onChange={(value) => onUpdate({ field: value, value: [] })}
          placeholder="Select Field"
          classes={{ root: "w-[160px]" }}
        />

        <SharedSelect
          options={getOperatorsForField(rule.field)}
          value={rule.operator}
          onChange={(value) =>
            onUpdate({
              operator: value,
              ...(value === "is_null" ? { value: [] } : {}),
            })
          }
          placeholder="Select operator"
          classes={{ root: "w-[120px]" }}
        />

        <div className="flex-1 min-w-[200px]">
          {error ? (
            <div className="text-red-500 text-sm">{error}</div>
          ) : isNullOperator ? (
            <>
            </>
          ) : isKeyValue ? (
            <div className="flex gap-2">
              <Input
                type="text"
                value={rule.value[0] || ""}
                onChange={(e) => {
                  const currentValue = rule.value[1] || "";
                  onUpdate({ value: [e.target.value, currentValue] });
                }}
                placeholder="Enter field key"
                className="w-full"
              />
              <Input
                type="text"
                value={rule.value[1] || ""}
                onChange={(e) => {
                  const currentKey = rule.value[0] || "";
                  onUpdate({ value: [currentKey, e.target.value] });
                }}
                placeholder="Enter field value"
                className="w-full"
              />
            </div>
          ) : isNumber || isNumericOperator ? (
            <Input
              type="number"
              value={rule.value[0] || ""}
              onChange={(e) => onUpdate({ value: [e.target.value] })}
              placeholder="Enter value"
              className="w-full"
            />
          ) : isString || isCustomMetadataField ? (
            <Input
              type="text"
              value={rule.value[0] || ""}
              onChange={(e) => onUpdate({ value: [e.target.value] })}
              placeholder="Enter value"
              className="w-full"
            />
          ) : isBoolean ? (
            <SharedSelect
              options={[
                { label: "Allowed", value: "true" },
                { label: "Not Allowed", value: "false" },
              ]}
              value={rule.value[0] || ""}
              onChange={(value) => onUpdate({ value: [value] })}
              placeholder={isLoading ? "Loading..." : "Select value"}
              classes={{ root: "w-full" }}
            />
          ) : (
            <MultiSelect
              options={getOptionsForField(rule.field)}
              value={rule.value}
              onChange={(values) => onUpdate({ value: values })}
              placeholder={isLoading ? "Loading..." : "Select values"}
            />
          )}
        </div>
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={onRemove}
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Remove rule</span>
      </Button>
    </div>
  );
}
